import {
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Input, Type } from '@/components/form/Input';
import { Button } from '@/components/ui/button';

interface HeaderRow {
  key: string;
  value: string;
}

interface StaticHeadersFormProps {
  label: string;
  onLabelChange: (label: string) => void;
  headers: HeaderRow[];
  onHeadersChange: (headers: HeaderRow[]) => void;
  showLabel?: boolean;
  // When true, header VALUE inputs are masked (type=password) with a reveal
  // toggle, and autofill is disabled (security P1-3). Header KEY inputs are
  // never masked — only the secret value is sensitive. Defaults to false so
  // existing callers are unaffected.
  secret?: boolean;
}

export function StaticHeadersForm({
  label,
  onLabelChange,
  headers,
  onHeadersChange,
  showLabel = true,
  secret = false,
}: StaticHeadersFormProps) {
  const [revealValues, setRevealValues] = useState(false);

  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    const updated = headers.map((h, i) =>
      i === index ? { ...h, [field]: val } : h
    );
    onHeadersChange(updated);
  };

  const addHeader = () => {
    onHeadersChange([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    if (headers.length <= 1) return;
    onHeadersChange(headers.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {showLabel && (
        <Input
          label="Label"
          name="static-header-label"
          value={label}
          onChange={e => onLabelChange(e.target.value)}
          placeholder="e.g. Production API Token"
          data-testid="static-header-label-input"
        />
      )}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-chariot-text-primary block text-sm font-medium">
            Headers
          </label>
          {secret && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevealValues(prev => !prev)}
              aria-label={revealValues ? 'Hide values' : 'Reveal values'}
              data-testid="static-header-reveal-toggle"
            >
              {revealValues ? (
                <EyeSlashIcon className="size-4" />
              ) : (
                <EyeIcon className="size-4" />
              )}
              {revealValues ? 'Hide' : 'Reveal'}
            </Button>
          )}
        </div>
        {headers.map((header, index) => (
          <div key={index} className="mb-2 flex items-end gap-2">
            <div className="flex-1">
              <Input
                name={`header-key-${index}`}
                placeholder={index === 0 ? 'Authorization' : 'Header name'}
                value={header.key}
                onChange={e => updateHeader(index, 'key', e.target.value)}
                data-testid={`header-key-${index}`}
              />
            </div>
            <div className="flex-1">
              <Input
                name={`header-value-${index}`}
                type={secret && !revealValues ? Type.PASSWORD : undefined}
                placeholder={index === 0 ? 'Bearer eyJ...' : 'Header value'}
                value={header.value}
                onChange={e => updateHeader(index, 'value', e.target.value)}
                autoComplete={secret ? 'off' : undefined}
                data-1p-ignore={secret || undefined}
                data-lpignore={secret ? 'true' : undefined}
                data-testid={`header-value-${index}`}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeHeader(index)}
              disabled={headers.length <= 1}
            >
              <TrashIcon className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={addHeader}
          data-testid="add-static-header-button"
        >
          <PlusIcon className="size-4" />
          Add Header
        </Button>
      </div>
    </div>
  );
}
